import React, { useState, useEffect } from 'react';
import './Contact.css';
import { getApiBaseUrl } from '../config/api';

export default function Contact() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    engagement: '',
    budget: '',
    timeline: '',
    brief: '',
    consent: false
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitRef, setSubmitRef] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Required';
    if (!formData.email.trim()) newErrors.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email';
    if (!formData.consent) newErrors.consent = 'Required';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/enquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          industry: formData.role,
          projectInterest: formData.engagement,
          message: formData.brief,
          source: 'website-form'
        })
      });

      if (response.ok) {
        const ref = 'KCT-' + Math.random().toString(36).slice(2, 7).toUpperCase();
        setSubmitRef(ref);
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          company: '',
          role: '',
          engagement: '',
          budget: '',
          timeline: '',
          brief: '',
          consent: false
        });
      }
    } catch (error) {
      console.error('Error submitting enquiry:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="section shell" id="contact" style={{ paddingBottom: 0 }}>
      <div className="enquire" id="enquire">
        <div className="enquire__container">
          <div className="enquire__head">
            <div className="section-label reveal">07 — Enquiry</div>
            <h3 className="display enquire__title reveal" style={{ marginBottom: 0, marginTop: '32px' }}>
              Start a<br /><em>conversation.</em>
            </h3>
            <p className="enquire__description reveal">
              Tell us about the platform, the timeline and the regulatory environment. A partner will reply within one business day. All enquiries handled under NDA.
            </p>
            <ul className="enquire__bullets reveal">
              <li>Replies within 1 business day</li>
              <li>NDA on request</li>
            </ul>
          </div>

          <form className="enquire__form reveal" id="enquire-form" onSubmit={handleSubmit} noValidate>
            <div className="field-grid">
              <label className="field">
                <span className="field__label">/01 — Full name</span>
                <input
                  className={`field__input ${errors.name ? 'is-invalid' : ''}`}
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  autoComplete="name"
                  placeholder="Jane Marchetti"
                />
              </label>
              <label className="field">
                <span className="field__label">/02 — Work email</span>
                <input
                  className={`field__input ${errors.email ? 'is-invalid' : ''}`}
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  autoComplete="email"
                  placeholder="jane@firm.com"
                />
              </label>
              <label className="field">
                <span className="field__label">/03 — Company</span>
                <input
                  className="field__input"
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  autoComplete="organization"
                  placeholder="Northbank Securities"
                />
              </label>
              <label className="field">
                <span className="field__label">/04 — Role</span>
                <input
                  className="field__input"
                  type="text"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  autoComplete="organization-title"
                  placeholder="Head of Platform"
                />
              </label>
            </div>

            <fieldset className="field field--full" style={{ border: 'none', padding: 0 }}>
              <legend className="field__label">/05 — Type of engagement</legend>
              <div className="chips" role="radiogroup">
                {['Trading & execution', 'Wealth & advisory', 'Risk & analytics', 'Payments & ledger', 'Compliance / RegTech', 'Data, AI & quant', 'Other'].map(type => (
                  <label key={type} className="chip">
                    <input
                      type="radio"
                      name="engagement"
                      value={type}
                      checked={formData.engagement === type}
                      onChange={handleChange}
                    />
                    <span>{type}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <div className="field-grid">
              <label className="field">
                <span className="field__label">/06 — Indicative budget</span>
                <select className="field__input field__select" name="budget" value={formData.budget} onChange={handleChange}>
                  <option value="">Select range</option>
                  <option>$10k — $25k</option>
                  <option>$25k — $200k</option>
                  <option>$200k — $500k</option>
                  <option>$500k — $1m</option>
                  <option>$1m+</option>
                  <option>To be scoped</option>
                </select>
              </label>
              <label className="field">
                <span className="field__label">/07 — Target start</span>
                <select className="field__input field__select" name="timeline" value={formData.timeline} onChange={handleChange}>
                  <option value="">Select timeline</option>
                  <option>Within 4 weeks</option>
                  <option>Q3 2026</option>
                  <option>Q4 2026</option>
                  <option>H1 2027</option>
                  <option>Exploratory</option>
                </select>
              </label>
            </div>

            <label className="field field--full">
              <span className="field__label">/08 — Brief</span>
              <textarea
                className="field__input field__textarea"
                name="brief"
                value={formData.brief}
                onChange={handleChange}
                rows="5"
                placeholder="A few sentences on the platform, the regulatory environment, and what success looks like."
              ></textarea>
            </label>

            <label className="consent">
              <input
                type="checkbox"
                name="consent"
                checked={formData.consent}
                onChange={handleChange}
              />
              <span>I consent to K Capital Technology storing this enquiry for the purpose of responding. We do not share or use it for marketing.</span>
            </label>

            <div className="enquire__submit">
              <button type="submit" className="btn-submit" disabled={loading}>
                <span>{loading ? 'Sending…' : 'Send enquiry'}</span>
                <span className="btn-submit__arrow">↗</span>
              </button>
              <span className="enquire__hint">Or email <a href="mailto:Andrew@k-capital.com.au">Andrew@k-capital.com.au</a> directly.</span>
            </div>
          </form>

        </div>
      </div>

      {submitted && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          backdropFilter: 'blur(4px)'
        }} onClick={() => setSubmitted(false)}>
          <div style={{
            backgroundColor: 'var(--bg)',
            border: '1px solid rgba(244, 241, 234, 0.1)',
            borderRadius: '8px',
            padding: '48px',
            maxWidth: '500px',
            textAlign: 'center',
            cursor: 'auto'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: 'rgba(111, 207, 151, 0.15)',
              border: '2px solid #6FCF97',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              fontSize: '32px'
            }}>✓</div>
            <h2 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '12px', color: 'var(--ivory)' }}>Enquiry received.</h2>
            <p style={{ fontSize: '15px', color: 'rgba(244, 241, 234, 0.7)', lineHeight: '1.6', marginBottom: '24px' }}>
              Thank you — a partner will reply within one business day.
            </p>
            <div style={{ fontSize: '13px', color: 'rgba(244, 241, 234, 0.5)', marginBottom: '32px', fontFamily: 'var(--mono)' }}>
              Reference: <strong style={{ color: 'var(--ivory)' }}>{submitRef}</strong>
            </div>
            <button
              onClick={() => setSubmitted(false)}
              style={{
                padding: '12px 32px',
                backgroundColor: 'rgba(244, 241, 234, 0.1)',
                border: '1px solid rgba(244, 241, 234, 0.2)',
                borderRadius: '4px',
                color: 'var(--ivory)',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(244, 241, 234, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'rgba(244, 241, 234, 0.1)';
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
